<template>
  <div class="w-fit mx-auto">
    <CommonSpinner size="2rem" />
  </div>
</template>

<script setup lang="ts">
import type { GetStartedService } from "../../types";
import { createStaleResponseGuard } from "~/utils/staleResponseGuard";

const route = useRoute();
const getStartedModalStore = useGetStartedModalStore();
const mapStore = useMapStore();

/**
 * Selected service entity.
 */
const selectedService = computed(() => getStartedModalStore.selectedService);

/**
 * Get clinic slug from route query params.
 */
const selectedClinicSlug = computed(() => route.query.slug);

/**
 * Lets a slow response for an earlier clinic be ignored once another clinic is selected,
 * or this component unmounts.
 */
const clinicRequests = createStaleResponseGuard();

/**
 * Fetch clinic detail by selected slug.
 */
const fetchClinicDetailBySlug = async () => {
  // A cleared slug deliberately doesn't invalidate the in-flight request: browser Back leaves the
  // modal on the tests step, so cancelling here would leave the panel on the loader for good.
  if (!selectedClinicSlug.value) return;
  const requestId = clinicRequests.start();

  // Start loading before the clinic detail fetch, so the tests panel never shows the previous
  // clinic's tests or "no tests available" while this clinic is being looked up.
  mapStore.startPanelTestsLoading();
  try {
    const { data: clinicDetail } = await useFetchClinicDetailBySlug(selectedClinicSlug.value);
    const clinic = clinicDetail.value?.data;
    if (!clinic || !clinicRequests.isCurrent(requestId)) return;

    mapStore.setSelectedClinic({
      id: clinic.id,
      title: clinic.display_title,
      slug: clinic.slug,
    });
    await fetchClinicTests(clinic.id, requestId);
  } finally {
    if (clinicRequests.isCurrent(requestId)) {
      mapStore.setPanelTestsLoading(false);
    }
  }
};

/**
 * Fetch all tests related to a specific clinic.
 * Get started → instant test should show both `instant-test` and `clinic-test` products,
 * but `clinic-test` is only requested when that service is enabled site-wide so
 * deployments without it keep their previous behaviour.
 * @param clinic - Clinic Id from where we should fetch tests.
 * @param requestId - Id from `clinicRequests`; the result is dropped if it is no longer current.
 */
const fetchClinicTests = async (clinic: string, requestId: number) => {
  const { data: servicesResult } = await useServiceListType();
  const services: GetStartedService[] = servicesResult.value?.data ?? [];
  const isClinicTestEnabled = services.some((service) => service.slug === "clinic-test");

  const params: Record<string, string> = {
    clinic,
    "service_type[0]": "instant-test",
  };
  if (isClinicTestEnabled) {
    params["service_type[1]"] = "clinic-test";
  }

  const { data: productsList } = await useFetchProductList(params);
  if (!clinicRequests.isCurrent(requestId)) return;
  mapStore.getTests(productsList.value?.data ?? []);
};

/**
 * Watch selected clinic slug change in order to update the products.
 */
watch(selectedClinicSlug, fetchClinicDetailBySlug);

/**
 * Drop any in-flight response when leaving, so it can't overwrite another page's panel tests.
 */
onBeforeUnmount(clinicRequests.invalidate);

/**
 * Auto call at first render
 */
await fetchClinicDetailBySlug();

function openModal() {
  getStartedModalStore.navigate("select_clinic");
  getStartedModalStore.open(
    // @ts-ignore
    {
      clinic_view: "map",
      ...selectedService.value,
      content: "",
    }
  );
}

async function abort() {
  navigateTo({
    params: {
      ...route.params,
      service: "",
    },
  });
}

onMounted(openModal);
watch(
  () => getStartedModalStore.isOpen,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) abort();
  }
);

onUnmounted(getStartedModalStore.close);
</script>
